import { Controller } from 'react-hook-form';
import { InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'shared/components/FormControl';

import RichTextEditor from 'dashboard/components-lib/RichTextEditor';
import { CaseCommentTemplate } from 'dashboard/models/case-template';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface FormEditCaseCommentTemplateProps {
  defaultValues: FormEditCaseCommentTemplateData;
  handleUpdateCommentTemplate: (updatedCommentTemplate: CaseCommentTemplate) => void;
  handleClose: () => void;
}

const FormEditCaseCommentTemplate = ({
  defaultValues,
  handleUpdateCommentTemplate,
  handleClose,
}: FormEditCaseCommentTemplateProps & InjectedFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useFormSecurely<FormEditCaseCommentTemplateData>({
    defaultValues,
    mode: 'onChange',
  });

  return (
    <form onSubmit={handleSubmit(handleUpdateCommentTemplate)}>
      <Label id='form-edit-case-comment-template__name_label' size='md' weight='bold'>
        Template name
      </Label>
      <Input
        {...register('name', {
          required: 'Comment templates must have a name.',
          pattern: {
            value: /^[_a-zA-Z0-9][_a-zA-Z0-9\s]*$/,
            message: 'Please use letters, numbers, underscores, or spaces only.',
          },
        })}
        disabled={isSubmitting}
        role='input'
        aria-labelledby='form-edit-case-comment-template__name_label'
      />
      {errors['name'] && <FormError>{errors['name'].message}</FormError>}
      <Spacer height={1} />
      <Label size='md' weight='bold'>
        Comment
      </Label>
      <Controller
        name='body'
        control={control}
        render={({ field: { value, onChange } }) => (
          <RichTextEditor defaultValue={value} onChange={onChange} />
        )}
      />
      <Spacer height={2} />
      <HStack equal={true}>
        <Button
          id='form-edit-case-comment-template__cancel_button'
          variant='basic'
          disabled={isSubmitting}
          isPending={isSubmitting}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          type='submit'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
        >
          Update
        </Button>
      </HStack>
    </form>
  );
};

export interface FormEditCaseCommentTemplateData {
  name: string;
  token: string;
  body: string;
}

export default reduxForm<FormEditCaseCommentTemplateData, FormEditCaseCommentTemplateProps>({
  form: 'editCaseCommentTemplate',
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormEditCaseCommentTemplate);
